import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { getBrand } from '../constants/brands';
import { HOME_PAGE_SETTINGS } from '../services/brandService/queries';
import { HomePageSettings, sanitizeHomePage } from '../utils/homePageSettings';
import { readHomePageCache, writeHomePageCache } from '../../config/homePageCache';

/** Spec 029 D6: with nothing cached, the words wait this long for the API. */
export const HIDE_UNTIL_ANSWER_MS = 1500;

export type HomePageSettingsState = {
  /** The banner key to show; null = the standard photo (once `bannerResolved`). */
  bannerKey: string | null;
  /** False while there is no cached or fresh answer yet (keep the photo blank). */
  bannerResolved: boolean;
  /** Null fields mean the built-in defaults. */
  homePage: HomePageSettings | null;
  /** The words can be shown: cached, answered, failed, or the wait ran out. */
  ready: boolean;
};

/**
 * Spec 029 US4: the home page settings from this browser's cache first, then
 * the API. A good answer replaces the cache; a failure or an older API keeps
 * what was cached (or the built-in defaults).
 */
export function useHomePageSettings(
  brandSite: string = getBrand().brandSite,
): HomePageSettingsState {
  const [cached] = useState(() => readHomePageCache(brandSite));
  const [timedOut, setTimedOut] = useState(false);
  const { data, error } = useQuery(HOME_PAGE_SETTINGS, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const answered = !!data && !error;
  const brand = data?.currentBrand ?? null;

  useEffect(() => {
    if (cached) return undefined;
    const timer = setTimeout(() => setTimedOut(true), HIDE_UNTIL_ANSWER_MS);
    return () => clearTimeout(timer);
  }, [cached]);

  useEffect(() => {
    if (!answered) return;
    writeHomePageCache(brandSite, {
      homeBannerImageKey: brand?.homeBannerImageKey ?? null,
      homePage: brand?.homePage ?? null,
    });
  }, [answered, brand, brandSite]);

  const homePage = useMemo(() => {
    if (answered) return sanitizeHomePage(brand?.homePage);
    return cached?.homePage ?? null;
  }, [answered, brand, cached]);

  const bannerKey = answered
    ? brand?.homeBannerImageKey?.trim() || null
    : (cached?.homeBannerImageKey ?? null);

  return {
    bannerKey,
    bannerResolved: answered || !!error || !!cached,
    homePage,
    ready: !!cached || answered || !!error || timedOut,
  };
}
